import { RefreshOutlined } from "@mui/icons-material";
import { Box, Button, Typography } from "@mui/material";
import { format } from "date-fns";
import React from "react";
import { useTranslation } from "react-i18next";

import colors from "../../../config/colors";
import { useDateFnsLocale } from "../../../utils/date-locale";

interface HomeHeaderProps {
	firstName?: string;
	fetchedAt: string | null;
	isLoading: boolean;
	onRefresh: () => void;
}

function HomeHeader({
	firstName,
	fetchedAt,
	isLoading,
	onRefresh,
}: HomeHeaderProps) {
	const { t } = useTranslation();
	const dateLocale = useDateFnsLocale();

	return (
		<Box
			sx={{
				display: "flex",
				alignItems: "flex-end",
				justifyContent: "space-between",
				flexWrap: "wrap",
				gap: 2,
				padding: "20px 24px 18px",
				borderBottom: "1px solid rgba(0, 0, 0, 0.12)",
			}}
		>
			<Box>
				<Typography
					variant='h5'
					component='h1'
					sx={{ fontWeight: 400, color: colors.primary }}
				>
					{firstName
						? t("supervisor-home_greeting", { name: firstName })
						: t("supervisor-home_title")}
				</Typography>
				<Typography sx={{ fontSize: "13px", color: colors.gray, mt: 0.5 }}>
					{/* "PPPP" is the locale's own full date format, so the weekday
					and month names and their order follow the UI language. */}
					{format(new Date(), "PPPP", { locale: dateLocale })}
				</Typography>
			</Box>

			<Box
				sx={{
					display: "flex",
					alignItems: "center",
					flexWrap: "wrap",
					gap: 1.5,
				}}
			>
				{fetchedAt ? (
					<Typography sx={{ fontSize: "12px", color: colors.gray }}>
						{t("supervisor-home_updated-at", {
							// "p" is the locale's own time format (12-hour in
							// English, 24-hour in French and Spanish).
							time: format(new Date(fetchedAt), "p", { locale: dateLocale }),
						})}
					</Typography>
				) : null}
				<Button
					variant='outlined'
					startIcon={<RefreshOutlined />}
					onClick={onRefresh}
					disabled={isLoading}
				>
					{t("supervisor-home_refresh")}
				</Button>
			</Box>
		</Box>
	);
}

export default HomeHeader;
